// src/components/ForcePasswordChange.js
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { toast } from 'react-toastify';
import { FaSpinner } from 'react-icons/fa';

import './Login.css'; // Usando o CSS de login para garantir que o visual fique perfeito
import logoGuincho from '../logo_guincho.png';
import backgroundGuincho from '../guinchotr.jpg';

// Reutilizando o ícone de olho para manter o padrão visual e melhorar a UX
const EyeIcon = ({ visible }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20" height="20" viewBox="0 0 24 24"
        fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    >
        {visible ? (
            <>
                <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                <circle cx="12" cy="12" r="3" />
            </>
        ) : (
            <>
                <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
                <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
                <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
                <line x1="2" x2="22" y1="2" y2="22" />
            </>
        )}
    </svg>
);

export default function ForcePasswordChange() {
    const navigate = useNavigate();
    const location = useLocation();
    
    // Captura o userId do state (passado pelo navigate('/definir-nova-senha', { state: { userId } }))
    const userId = location.state?.userId;

    const [passwords, setPasswords] = useState({ newPassword: '', confirmPassword: '' });
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Se o usuário cair nessa tela de paraquedas sem um ID, manda pro login
    useEffect(() => {
        if (!userId) {
            navigate('/login');
        }
    }, [userId, navigate]);

    const handleChange = (e) => {
        const { id, value } = e.target;
        setPasswords(prev => ({ ...prev, [id]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        // Remove espaços acidentais no início ou no fim
        const newPass = passwords.newPassword.trim();
        const confirmPass = passwords.confirmPassword.trim();

        if (newPass !== confirmPass) {
            setError('As senhas não coincidem. Verifique e tente novamente.');
            return;
        }

        if (newPass.length < 6) {
            setError('A senha deve ter no mínimo 6 caracteres.');
            return;
        }

        setLoading(true);
        try {
            // AQUI ESTÁ A MÁGICA: Enviando 'senha' em vez de 'newPassword'
            const response = await api.post('/api/usuarios/set-initial-password', {
                userId: userId,
                senha: newPass 
            });
            
            const successMsg = response.data.message || 'Senha alterada com sucesso!';
            setSuccess(successMsg);
            toast.success(successMsg);
            
            // Aguarda 3 segundos para o usuário ler a mensagem e manda pro login
            setTimeout(() => {
                navigate('/login');
            }, 3000);

        } catch (err) {
            const errorMsg = err.response?.data?.error || 'Ocorreu um erro ao atualizar a senha.';
            setError(errorMsg);
            toast.error(errorMsg); // Mantive o toast caso você prefira, mas o erro inline previne telas gigantes
        } finally {
            setLoading(false);
        }
    };

    if (!userId) return null; // Evita renderizar a tela vazia antes do redirect

    // Reutilizando o fundo padrão do login
    const backgroundStyle = { 
        backgroundImage: `linear-gradient(rgba(16, 28, 93, 0.7), rgba(16, 28, 93, 0.7)), url(${backgroundGuincho})` 
    };

    return (
        <div className="login-background" style={backgroundStyle}>
            <div className="login-box">
                <img src={logoGuincho} alt="Logo" className="login-logo" />
                <h2 className="login-title">Primeiro Acesso</h2>
                
                {!success ? (
                    <form onSubmit={handleSubmit}>
                        <p className="info-text" style={{ textAlign: 'center', marginBottom: '20px' }}>
                            Para sua segurança, por favor, defina uma nova senha para continuar.
                        </p>
                        
                        <div className="form-group password-wrapper">
                            <label htmlFor="newPassword">Nova Senha:</label>
                            <input
                                type={showPassword ? 'text' : 'password'}
                                id="newPassword"
                                value={passwords.newPassword}
                                onChange={handleChange}
                                placeholder="********"
                                required
                            />
                            <span
                                className="password-toggle-icon"
                                onClick={() => setShowPassword(p => !p)}
                                aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                                role="button"
                                style={{ cursor: 'pointer' }}
                            >
                                <EyeIcon visible={showPassword} />
                            </span>
                        </div>

                        <div className="form-group password-wrapper">
                            <label htmlFor="confirmPassword">Confirme a Nova Senha:</label>
                            <input
                                type={showPassword ? 'text' : 'password'}
                                id="confirmPassword"
                                value={passwords.confirmPassword}
                                onChange={handleChange}
                                placeholder="********"
                                required
                            />
                        </div>

                        {/* Container de erro blindado para não quebrar a tela */}
                        {error && (
                            <div className="alert-error-container" style={{ marginBottom: '15px' }}>
                                <span>{error}</span>
                            </div>
                        )}
                        
                        <button type="submit" className="login-button" disabled={loading}>
                            {loading ? <FaSpinner className="spinner" /> : 'Definir Senha e Continuar'}
                        </button>
                    </form>
                ) : (
                    <div className="final-message-box" style={{ textAlign: 'center' }}>
                        <h3 style={{ color: '#4CAF50', marginBottom: '10px' }}>Tudo certo!</h3>
                        <p className="success-message" style={{ marginBottom: '15px', fontWeight: 'bold' }}>{success}</p>
                        <p style={{ fontSize: '0.9rem', color: '#666' }}>
                            Você será redirecionado para a tela de login em instantes...
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}